import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  FaMapMarkerAlt,
  FaUser,
  FaPencilAlt,
  FaExclamationTriangle,
  FaInfoCircle,
} from "react-icons/fa";
import { Button } from "../../components/ui/button";
import { DataTable } from "../../components/DataTable";
import { ArrowUpDown } from "lucide-react";
import { GoLog, GoPlusCircle } from "react-icons/go";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import Modal from "../../components/Modal";
import { LuScanQrCode } from "react-icons/lu";
import {
  POCService,
  membershipService,
  profileService,
  transactionService,
} from "../../services";
import useUserStore from "../../stores/userStore";
import { checkinService } from "../../services/checkinService";
import MaterialSelect from "../../components/Materialselect";
import useMaterialStore from "../../stores/materialStore";
import ShortenedText from "../../components/ShortenedText";
import useUserStore2 from "../../stores/userStore2";

const columns = [
  {
    accessorKey: "transactionId",
    header: "No.",
    cell: ({ row }) => <span>{row.getValue("transactionId")}</span>,
  },
  {
    accessorKey: "selectedMaterial",
    header: "Material Category",
    cell: ({ row }) => <span>{row.getValue("selectedMaterial")}</span>,
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    cell: ({ row }) => <span>{row.getValue("quantity")}</span>,
  },
];

const POCAttendant = () => {
  const [scannedPOC, setScannedPOC] = useState(null);
  const [scannedMember, setScannedMember] = useState(null);
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [materialCategory, setMaterialCategory] = useState("");
  const [quantity, setQuantity] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [transactionToEdit, setTransactionToEdit] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewPOCModal, setViewPOCModal] = useState(false);
  const [viewMemberModal, setViewMemberModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [logoutModal, setLogoutModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submittedTransactions, setSubmittedTransactions] = useState([]);
  const [showSubmittedTransactionsModal, setShowSubmittedTransactionsModal] =
    useState(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setMaterialCategory("");
    setQuantity("");
  };

  const handleSubmit = () => {
    const newTransaction = {
      transactionId: `${transactions.length + 1}`,
      selectedMaterial,
      quantity: quantity,
    };
    setTransactions([...transactions, newTransaction]);
    setIsSubmitDisabled(false);
    handleModalClose();
  };

  const handleConfirmationSubmit = async () => {
    if (transactions.length === 0) {
      alert("No transactions to submit.");
      return;
    }

    if (!scannedMember) {
      alert("No member scanned. Please scan a member QR code first.");
      return;
    }

    if (!scannedPOC) {
      alert("No POC scanned. Please scan a POC QR code first.");
      return;
    }

    setIsSubmitDisabled(true);

    try {
      setIsSubmitting(true);
      const { profileId } = scannedMember;
      const { pocId } = scannedPOC;
      const userId = useUserStore.getState().userId;
      const materials = useMaterialStore.getState().materials;

      console.log("Transactions to submit:", transactions);
      console.log("Available materials:", materials);

      const payload = {
        Transactions: transactions
          .map((tx) => {
            console.log(`Processing Material: ${tx.selectedMaterial}`);

            // Find material by category name
            let material = materials.find(
              (m) => m.category === tx.selectedMaterial
            );

            if (!material) {
              console.error(`Material '${tx.selectedMaterial}' not found.`);
              return null;
            }

            let selectedMaterial = material; // Default to parent material

            // Check if the selected material has sub-materials
            if (material?.sub_material?.length) {
              const subMaterial = material.sub_material.find(
                (sub) => sub.category === tx.selectedMaterial
              );
              if (subMaterial) {
                selectedMaterial = subMaterial;
              }
            }

            console.log(
              `Final Material ID: ${selectedMaterial._id}, Points: ${selectedMaterial.point}`
            );

            return {
              ProfileId: profileId,
              PocId: pocId,
              PocAttendantId: userId,
              MaterialId: selectedMaterial._id,
              TransactionUnit: selectedMaterial.unit || "kg",
              TransactionPoint: selectedMaterial.point || 0,
              Quantity: Number(tx.quantity),
            };
          })
          .filter((tx) => tx !== null), // Remove null transactions
      };

      console.log("Final Payload:", JSON.stringify(payload, null, 2));

      const response = await transactionService.addTransactions(payload);
      console.log("Submission successful:", response);

      // Save submitted transactions
      setSubmittedTransactions(response.Data || []);
      setShowSubmittedTransactionsModal(true);

      // Reset the state after successful submission
      setTransactions([]);
      setScannedMember(null);
      setIsSubmitting(false);
      setIsConfirmationModalOpen(false);
    } catch (error) {
      console.error("Failed to submit transactions:", error);
      alert("Transaction submission failed. Please try again.");
      setIsSubmitting(false);
    } finally {
      setIsSubmitDisabled(false);
    }
  };

  const handleDeleteClick = (transactionId) => {
    setTransactionToDelete(transactionId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (transactionToDelete) {
      setTransactions((prevTransactions) =>
        prevTransactions.filter(
          (transaction) => transaction.transactionId !== transactionToDelete
        )
      );
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    }
  };

  const actions = (row) => [
    {
      label: "Edit details",
      icon: <FaPencilAlt />,
      onClick: () => {
        setTransactionToEdit({
          transactionId: row.transactionId,
          selectedMaterial: row.selectedMaterial,
          quantity: row.quantity,
        });
        setIsOpen(true);
      },
    },
    {
      label: "Delete",
      icon: <FaExclamationTriangle />,
      variant: "dropRed",
      onClick: () => handleDeleteClick(row.transactionId),
    },
  ];

  const handleScanSuccess = async (decodedText) => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 100));

      stopScanning();

      if (!scannedPOC) {
        const parts = decodedText.split("/");
        const pocId = parts.length > 2 ? parts[2] : null;

        if (!pocId) {
          setError("Invalid POC QR Code. Please scan a valid POC QR code.");
          setLoading(false);
          return;
        }

        try {
          const response = await POCService.getPOCDetails(pocId);
          if (response?.Data?.length > 0) {
            const pocData = response.Data[0];

            const scannedPOCData = {
              pocId: pocData._id,
              name: pocData.name,
              address1: pocData.address_line1,
              address2: pocData.address_line2,
              postcode: pocData.postcode,
              city: pocData.city,
              state: pocData.state,
              country: pocData.country,
            };

            setScannedPOC(scannedPOCData);
            setError(null);
            await handleCheckin(scannedPOCData);
          } else {
            setError("POC details not found. Please try again.");
          }
        } catch (apiError) {
          setError("Failed to fetch POC details. Please try again.");
        }
      } else if (!scannedMember) {
        const parts = decodedText.split("/");
        if (parts.length !== 2) {
          setError("Invalid Member QR Code format. Please try again.");
          setLoading(false);
          return;
        }

        const [scannedEntityId, profileId] = parts;

        if (!scannedEntityId || !profileId) {
          setError(
            "Invalid Member QR Code. Please scan a valid Member QR code."
          );
          setLoading(false);
          return;
        }

        try {
          const response = await profileService.getProfiles();
          const allProfiles = response?.Data || [];

          const matchingProfile = allProfiles.find(
            (profile) => profile._id === profileId
          );

          if (!matchingProfile) {
            setError("Profile not found. Please scan a valid QR code.");
            setLoading(false);
            return;
          }

          setScannedMember({
            entityId: scannedEntityId,
            profileId: matchingProfile._id,
            role: matchingProfile.role || "member", // fallback role if needed
            communityId: matchingProfile.communityId || null,
            communityName: matchingProfile.communityName || "Unknown",
            groupName: matchingProfile.groupName || "Unknown",
            groupId: matchingProfile.groupId || null,
            status: matchingProfile.status || "active",
            name: matchingProfile.name || "Unnamed",
            entityName: matchingProfile.entity.name || "Unnamed",
          });

          console.log("Matched profile: ", matchingProfile);
          setError(null);
        } catch (error) {
          setError("Failed to fetch membership details. Please try again.");
        }
      }
    } catch (error) {
      console.error("Failed to fetch profiles:", error);
      setError("Failed to fetch profile details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (scannedPOCData) => {
    try {
      setLoading(true);
      const { userId } = useUserStore.getState(); // Get userId from Zustand

      if (!scannedPOCData) {
        setError("POC data is missing. Check-in cannot proceed.");
        return;
      }

      const checkinData = {
        PocId: scannedPOCData.pocId,
        EntityId: userId,
      };

      console.log("Sending check-in data:", checkinData);

      const response = await checkinService.addCheckin(checkinData);

      if (response?.Data) {
        console.log("Check-in successful:", response);

        // Extract check-in time from API response
        const checkInTime = response.Data.create_date;

        // Store check-in state in localStorage
        const storedData = { ...scannedPOCData, checkInTime };
        localStorage.setItem("checkedInPOC", JSON.stringify(storedData));

        setScannedPOC(storedData); // Update state
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Check-in failed:", error);
      setError("Check-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleScanError = (err) => {
    console.warn("Scan Error:", err);
  };

  const handleCameraChange = async (newCameraId) => {
    try {
      if (scannerRef.current && isScanning) {
        await scannerRef.current.stop();
        setSelectedCamera(newCameraId);
        await scannerRef.current.start(
          newCameraId,
          { fps: 10, qrbox: { width: 250, height: 250 } },
          handleScanSuccess,
          handleScanError
        );
      }
    } catch (err) {
      setError("Failed to switch camera. Please try again.");
    }
  };

  const startScanning = async () => {
    if (
      isScanning ||
      (scannerRef.current && scannerRef.current.getState() === 2)
    ) {
      console.warn("Scanner is already running");
      return;
    }

    setError(null);
    setIsScanning(true);

    try {
      const devices = await Html5Qrcode.getCameras();
      if (devices.length === 0) {
        setError("No camera found. Please connect a camera.");
        return;
      }

      setCameras(devices);
      const cameraId = devices.at(-1).id;
      setSelectedCamera(cameraId);

      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("qr-scanner");
      }

      await scannerRef.current.start(
        cameraId,
        { fps: 10, qrbox: { width: 250, height: 250 } },
        handleScanSuccess,
        handleScanError
      );
    } catch (err) {
      setError(
        "Failed to access the camera. Check browser permissions and settings."
      );
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (scannerRef.current && scannerRef.current.getState() === 2) {
      scannerRef.current
        .stop()
        .then(() => {
          scannerRef.current.clear();
          setIsScanning(false);
        })
        .catch((err) => {
          console.error("Failed to stop scanning:", err);
        });
    }
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current && scannerRef.current.getState() === 2) {
        // Check if scanner is running
        scannerRef.current.stop().catch((err) => {
          console.error("Failed to stop scanner on unmount:", err);
        });
      }
      setCameras([]);
      setSelectedCamera("");
    };
  }, []);

  //used to check if user is currently checked in
  useEffect(() => {
    const storedPOC = localStorage.getItem("checkedInPOC");
    if (storedPOC) {
      setScannedPOC(JSON.parse(storedPOC)); // Restore the check-in state
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("checkedInPOC"); // Remove check-in state
    setScannedPOC(null); // Reset state
    window.location.reload(); // Refresh the page
  };

  const materials = useMaterialStore((state) => state.materials);

  const selectedMaterialObject = React.useMemo(() => {
    const parent = materials.find((m) => m.category === selectedMaterial);
    if (!parent) return null;

    if (parent.sub_material?.length) {
      const sub = parent.sub_material.find(
        (s) => s.category === selectedMaterial
      );
      return sub || parent;
    }

    return parent;
  }, [selectedMaterial, materials]);

  const getMaterialName = (materialId) => {
    const found = materials.find((m) => m._id === materialId);
    return found ? found.category : materialId;
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="px-4 sm:px-8 mt-2">
          <>
            <div className="flex flex-col md:flex-row gap-4 w-full">
              {scannedPOC && (
                <div
                  className={`bg-white shadow p-4 rounded-lg flex items-center ${
                    scannedMember ? "md:flex-1" : "w-full md:w-1/2"
                  }`}
                >
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="text-gray-600 text-4xl mr-3" />
                    <div>
                      <p
                        onClick={() => setLogoutModal(true)}
                        className="text-sm cursor-pointer text-green-600"
                      >
                        Successfully Checked-In &gt;
                      </p>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {scannedPOC.name}
                      </h3>
                    </div>
                  </div>
                  <FaInfoCircle
                    className="text-blue-500 text-2xl cursor-pointer ml-auto"
                    onClick={() => setViewPOCModal(true)}
                  />
                </div>
              )}

              {scannedMember && (
                <div className="bg-white shadow p-4 rounded-lg flex items-center w-full md:flex-1">
                  <div className="flex items-center">
                    <FaUser className="text-gray-600 text-3xl mr-3" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {scannedMember.name}
                    </h3>
                  </div>
                  <FaInfoCircle
                    className="text-blue-500 text-2xl cursor-pointer ml-auto"
                    onClick={() => setViewMemberModal(true)}
                  />
                </div>
              )}
            </div>
          </>
        </div>

        <div className="flex-1 p-4 sm:p-8">
          <div className="bg-white shadow rounded-lg p-4 sm:p-6 text-sm w-full h-full mx-auto px-4 sm:px-6 flex flex-col">
            <div className="w-full flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {scannedMember
                  ? "Transaction List"
                  : scannedPOC
                  ? "Member Verification"
                  : "POC Check-in"}
              </h2>

              {scannedMember && (
                <div className="flex gap-2 ml-auto">
                  <Button
                    variant={isSubmitDisabled ? "outline" : "hollowGreen"}
                    disabled={isSubmitDisabled}
                    onClick={() => setIsConfirmationModalOpen(true)}
                  >
                    Submit
                  </Button>
                  <Button onClick={() => setIsModalOpen(true)}>
                    <GoPlusCircle className="mr-2" /> Add
                  </Button>
                </div>
              )}
            </div>
            <hr className="mb-6 mt-4 border-gray-200" />

            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="animate-spin mx-auto rounded-full h-20 w-20 font-bold border-t-4 border-green-500"></div>
                <p className="mt-3 font-bold text-gray-800 text-lg">
                  Processing QR Code...
                </p>
              </div>
            ) : !scannedPOC || (scannedPOC && !scannedMember) ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <LuScanQrCode className="text-gray-500 text-6xl mb-4" />
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  {scannedPOC ? "Scan Member QR" : "You are not checked in"}
                </h2>
                <p className="text-gray-600 mb-6 text-center">
                  {scannedPOC
                    ? "Please scan the member’s QR code."
                    : "To get started, simply click the 'Scan QR Code' button below and scan your code to check in."}
                </p>
                <Button onClick={isScanning ? stopScanning : startScanning}>
                  {isScanning
                    ? "Stop Scanning"
                    : scannedPOC
                    ? "Scan Member QR Code"
                    : "Scan QR Code"}
                </Button>
                {isScanning && (
                  <div className="mt-4 w-full max-w-md">
                    <div className="flex flex-col gap-4">
                      <div id="qr-scanner"></div>
                      <Select
                        value={selectedCamera}
                        onValueChange={handleCameraChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select camera" />
                        </SelectTrigger>
                        <SelectContent>
                          {cameras.map((camera) => (
                            <SelectItem key={camera.id} value={camera.id}>
                              {camera.label || `Camera ${camera.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full">
                <DataTable
                  columns={columns}
                  data={transactions}
                  actions={actions}
                />
              </div>
            )}
          </div>
        </div>

        {/* Receipt modal */}
        <Modal
          isOpen={showSubmittedTransactionsModal}
          onClose={() => setShowSubmittedTransactionsModal(false)}
        >
          <div className="max-h-[80vh] w-auto overflow-y-auto text-sm sm:text-base">
            {/* Icon and title */}
            <div className="flex flex-col items-center mb-4">
              <div className="p-4 bg-teal-100 rounded-full mb-3">
                <GoLog className="text-teal-500 text-2xl" />
              </div>
              <h2 className="text-xl font-bold mb-2">Submission Successful!</h2>
            </div>

            {submittedTransactions.length > 0 && (
              <div className="p-4 bg-teal-50  text-sm  rounded-md mb-4">
                <div className="grid grid-cols-12 gap-y-2">
                  <p className="col-span-4 font-semibold text-left">Date:</p>
                  <p className="col-span-8 text-left">
                    {new Date(submittedTransactions[0].date).toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            <ul className="space-y-4">
              {submittedTransactions.map((tx) => (
                <li
                  key={tx._id}
                  className="p-4 bg-gray-50 border text-sm border-gray-100 rounded-md shadow-sm"
                >
                  <div className="grid grid-cols-12 gap-y-2">
                    <p className="col-span-4 font-semibold text-left">
                      Transaction ID:
                    </p>
                    <p className="col-span-8 break-all text-left">{tx._id}</p>

                    <p className="col-span-4 font-semibold text-left">
                      Material:
                    </p>
                    <p className="col-span-8 break-all text-left">
                      {getMaterialName(tx.material)}
                    </p>

                    <p className="col-span-4 font-semibold text-left">
                      Quantity:
                    </p>
                    <p className="col-span-8 text-left">
                      {tx.quantity} {tx.transaction_unit}
                    </p>

                    <p className="col-span-4 font-semibold text-left">
                      Total Points:
                    </p>
                    <p className="col-span-8 text-left">
                      {tx.transaction_point * tx.quantity}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex justify-end">
              <Button onClick={() => setShowSubmittedTransactionsModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </Modal>

        {/* Logout modal */}
        <Modal
          isOpen={logoutModal}
          onClose={() => {
            setLogoutModal(false);
          }}
        >
          <div className="">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Ready to Check Out?
            </h2>
            <p className="text-gray-600 mb-6">
              Looks like you're done!
              <br />
              Confirm your checkout to complete your visit.
            </p>
            <hr className="mb-6 border-gray-200" />
            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setLogoutModal(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleLogout}>
                Yes, Check out now.
              </Button>
            </div>
          </div>
        </Modal>

        {/* Member Details Modal */}
        <Modal
          isOpen={viewMemberModal}
          onClose={() => setViewMemberModal(false)}
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Member Details
            </h2>
          </div>

          {/* Member Information */}
          {/* Status Field with Formatting */}
          <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
            <span className="text-gray-500 w-32">Status</span>
            <div
              className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                scannedMember?.status === "A"
                  ? "text-[#16A34A] bg-[#DCFCE7]" // Green for Active
                  : scannedMember?.status === "D"
                  ? "text-[#EF4444] bg-[#FEE2E2]" // Red for Deactivated
                  : "text-[#FACC15] bg-[#FEF3C7]" // Yellow for anything else
              }`}
            >
              {scannedMember?.status === "A"
                ? "Active"
                : scannedMember?.status === "D"
                ? "Deactivated"
                : scannedMember?.status === "S"
                ? "Suspended"
                : "Unknown"}
            </div>
          </div>
          <div className="space-y-3 text-sm border-t pt-4">
            {[
              {
                label: "Entity ID",
                value: <ShortenedText text={scannedMember?.entityId} />,
              },
              {
                label: "Entity Name",
                value: <ShortenedText text={scannedMember?.entityName} />,
              },
              {
                label: "Profile ID",
                value: <ShortenedText text={scannedMember?.profileId} />,
              },

              { label: "Profile Name", value: scannedMember?.name },
            ].map((item, index) => (
              <div
                key={index}
                className="flex text-left bg-gray-50 px-3 py-2 rounded"
              >
                <span className="text-gray-500 w-32">{item.label}</span>
                <span className="text-gray-900 font-medium">{item.value}</span>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex justify-between gap-3 mt-6">
            <Button
              variant="outline"
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setViewMemberModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => setViewMemberModal(false)}>OK</Button>
          </div>
        </Modal>

        {/* POC Details Modal */}
        <Modal isOpen={viewPOCModal} onClose={() => setViewPOCModal(false)}>
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">POC Details</h2>
          </div>

          {/* Top Section: POC ID, Check-In Time, Status */}
          <div className="flex flex-wrap gap-x-4 gap-y-3 py-4 text-sm w-full justify-between">
            <div>
              <p className="text-gray-500 text-left">POC ID</p>
              <p className="font-medium text-gray-900">
                <ShortenedText text={scannedPOC?.pocId} />
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-left">Check-In Time</p>
              {scannedPOC?.checkInTime ? (
                <div className="font-medium text-gray-900">
                  <p>{new Date(scannedPOC.checkInTime).toLocaleDateString()}</p>
                  <p>{new Date(scannedPOC.checkInTime).toLocaleTimeString()}</p>
                </div>
              ) : (
                <p className="text-gray-900">N/A</p>
              )}
            </div>

            <div>
              <p className="text-gray-500 text-left">Status</p>
              <span
                className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium cursor-pointer"
                onClick={() => {
                  setLogoutModal(true);
                }}
              >
                Successfully Checked-in &gt;
              </span>
            </div>
          </div>

          {/* POC Information */}
          <div className=" text-sm border-t pt-4">
            {[
              { label: "POC Name", value: scannedPOC?.name },
              { label: "Address 1", value: scannedPOC?.address1 },
              { label: "Address 2", value: scannedPOC?.address2 },
              { label: "Postcode", value: scannedPOC?.postcode },
              { label: "City", value: scannedPOC?.city },
              { label: "State", value: scannedPOC?.state },
              { label: "Country", value: scannedPOC?.country },
            ].map((item, index) => (
              <div
                key={index}
                className={`flex text-left px-3 py-2 rounded ${
                  index % 2 === 0 ? "bg-gray-50" : "bg-white"
                }`}
              >
                <span className="text-gray-500 w-32">{item.label}</span>
                <span className="text-gray-900 font-medium">{item.value}</span>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex justify-between gap-3 mt-6">
            <Button
              variant="outline"
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setViewPOCModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={() => {
                setLogoutModal(true);
              }}
            >
              Check Out
            </Button>
          </div>
        </Modal>

        {/* add modal */}
        <Modal isOpen={isModalOpen} onClose={handleModalClose}>
          <div className="">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Add New Transaction
            </h2>
            <hr className="mb-6 border-gray-200" />

            <div className="space-y-4">
              <div>
                <Label htmlFor="material-category" className="block text-left">
                  Material Category
                </Label>
                <MaterialSelect
                  value={selectedMaterial}
                  onChange={(value) => {
                    setSelectedMaterial(value);
                  }}
                />
              </div>

              <div>
                <Label htmlFor="quantity" className="block text-left">
                  Quantity
                </Label>
                <div className="relative">
                  <Input
                    id="quantity"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Enter quantity"
                  />
                  <span className="absolute right-3 mr-4 top-2 text-gray-500">
                    {selectedMaterialObject?.unit || "unit"}
                  </span>
                </div>
              </div>
            </div>

            <hr className="mt-6 mb-4 border-gray-200" />

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>Save</Button>
            </div>
          </div>
        </Modal>

        {/* confirm submission modal */}
        <Modal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
        >
          <div className="">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Confirm Submission
            </h2>
            <p className="text-gray-600 mb-6">
              You're about to submit all your pending transactions. Please make
              sure everything is correct before proceeding.
            </p>
            <hr className="mb-6 border-gray-200" />
            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                onClick={() => setIsConfirmationModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmationSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">Submitting...</span>
                ) : (
                  "Submit Now"
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        >
          <div className="">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Delete Transaction
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this transaction? This action is
              NOT reversible and will result in permanent loss of this
              transaction.
            </p>
            <hr className="mb-6 border-gray-200" />
            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>

              <Button variant="destructive" onClick={handleConfirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit modal */}
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <div className="">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Edit Transaction
            </h2>
            <hr className="mb-6 border-gray-200" />

            <div className="space-y-4">
              <div>
                <Label htmlFor="material-category" className="block text-left">
                  Material Category
                </Label>
                <MaterialSelect
                  value={transactionToEdit?.selectedMaterial || ""}
                  onChange={(value) => {
                    setTransactionToEdit((prev) => ({
                      ...prev,
                      selectedMaterial: value, // Ensure the material is updated correctly
                    }));
                  }}
                />
              </div>

              <div>
                <Label htmlFor="quantity" className="block text-left">
                  Quantity
                </Label>
                <div className="relative">
                  <Input
                    id="quantity"
                    type="number"
                    value={
                      transactionToEdit?.quantity?.replace(" KG", "") || ""
                    }
                    onChange={(e) =>
                      setTransactionToEdit((prev) => ({
                        ...prev,
                        quantity: `${e.target.value} KG`,
                      }))
                    }
                    placeholder="Enter quantity"
                  />
                  <span className="absolute right-3 mr-4 top-2 text-gray-500">
                    KG
                  </span>
                </div>
              </div>
            </div>

            <hr className="mt-6 mb-4 border-gray-200" />

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const updatedTransactions = transactions.map((transaction) =>
                    transaction.transactionId ===
                    transactionToEdit.transactionId
                      ? transactionToEdit
                      : transaction
                  );
                  setTransactions(updatedTransactions);
                  setIsOpen(false); // Close the modal
                }}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Modal>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 mt-4 rounded-lg">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default POCAttendant;
